import { Button, Icon, IconTile, LinkButton, ProgressBar } from "@iec-id/ui";
import type { AuthSearch } from "@/lib/auth-flow";
import { api } from "@/lib/trpc";
import { OnboardingCard } from "./onboarding-card";
import { useOnboardingT } from "./onboarding-i18n";
import { StepTitle } from "./step-title";

/** Reply-to экосистемы (rules/integrations.md): поддержка отвечает почтой. */
const SUPPORT_MAILTO = "mailto:support@iec.exchange";

/**
 * Отказ по возрасту (инвариант №4, GDPR ст. 8).
 *
 * Экран не «ошибка формы», а конец сценария: аккаунт не активируется. Но
 * введённые данные остаются данными человека, и право их забрать (ст. 20) не
 * зависит от того, приняли мы его в экосистему или нет — поэтому выгрузка тут
 * настоящая, а не ссылка «напишите нам».
 */
export interface UnderAgeScreenProps {
	minAge: number;
	/** Контекст входа — карточка отдаёт его подвалу смены аккаунта. */
	search: AuthSearch;
	/**
	 * Вернуться к вводу даты. Существует потому, что сервер при отказе НЕ
	 * записывает введённую дату (решение D1): опечатка в году — самая частая
	 * причина попасть на этот экран, и человек, промахнувшийся мимо клавиши,
	 * не должен считать её концом истории.
	 */
	onCorrect: () => void;
}

export function UnderAgeScreen({ minAge, search, onCorrect }: UnderAgeScreenProps) {
	const t = useOnboardingT();

	const status = api.account.exportStatus.useQuery(undefined, {
		// Пока файл собирается, спрашиваем раз в три секунды; в остальных
		// состояниях опрос выключен — досбирать там нечего.
		refetchInterval: (query) => {
			const value = query.state.data?.state;
			return value === "queued" || value === "processing" ? 3000 : false;
		},
	});

	const utils = api.useUtils();
	const requestExport = api.account.exportData.useMutation({
		onSuccess: (result) => utils.account.exportStatus.setData(undefined, result),
	});

	const current = status.data;
	const busy =
		requestExport.isPending || current?.state === "queued" || current?.state === "processing";

	return (
		<OnboardingCard search={search}>
			<IconTile size={48} className="bg-warning-soft text-warning">
				<Icon name="CircleAlert" size={24} />
			</IconTile>

			<StepTitle>{t("underAge.title")}</StepTitle>
			<p className="t-body text-fg-secondary">{t("underAge.description", { minAge })}</p>

			<Button variant="second" fullWidth onClick={onCorrect}>
				{t("underAge.correct")}
			</Button>

			<div className="flex flex-col gap-2.5 rounded-control bg-sunken p-4">
				<span className="t-row text-fg">{t("underAge.exportTitle")}</span>
				<span className="t-meta text-fg-muted">{t("underAge.exportDescription")}</span>

				<div role="status" aria-live="polite" className="flex flex-col gap-2">
					{current?.state === "queued" ? (
						<span className="t-meta text-fg-muted">{t("underAge.exportQueued")}</span>
					) : null}
					{current?.state === "processing" ? (
						<>
							<span className="t-meta text-fg-muted">
								{t("underAge.exportProcessing", { progress: current.progress })}
							</span>
							<ProgressBar value={current.progress} label={t("underAge.exportTitle")} />
						</>
					) : null}
					{current?.state === "ready" ? (
						<span className="t-meta text-fg-muted">{t("underAge.exportReady")}</span>
					) : null}
					{current?.state === "failed" || requestExport.isError ? (
						<span className="t-meta text-danger">{t("underAge.exportFailed")}</span>
					) : null}
				</div>

				{current?.state === "ready" ? (
					<Button
						variant="second"
						fullWidth
						onClick={() => window.location.assign(current.downloadUrl)}
					>
						{t("underAge.exportDownload")}
					</Button>
				) : (
					<Button
						variant="second"
						fullWidth
						state={busy ? "loading" : "default"}
						onClick={() => requestExport.mutate({ format: "json", includePointsLedger: true })}
					>
						{t("underAge.exportAction")}
					</Button>
				)}
			</div>

			<p className="t-meta text-fg-muted">{t("underAge.comeBack", { minAge })}</p>
			<LinkButton href={SUPPORT_MAILTO} target="_blank" rel="noreferrer">
				{t("underAge.support")}
			</LinkButton>
		</OnboardingCard>
	);
}
